import { Chunk, Console, HashSet, pipe, Schema, SchemaGetter, String as Str } from 'effect';

import { getWarehouseCodeByUuid, getWarehouseUuidByCode } from '../../../tenant/warehouses';

import type { WarehouseFilterStateSet } from '../state/warehouse';
import { WarehouseFilterStateSet as WarehouseFilterStateSetSchema } from '../state/warehouse';

/**
 * API format constant for warehouse stock availability.
 * The backend expects "UUID:1" format where "1" indicates stock availability check.
 */
const WAREHOUSE_STOCK_AVAILABILITY = '1' as const;

const warehouseFilterStateSetToApiString = (warehouseSet: WarehouseFilterStateSet): string =>
  pipe(
    warehouseSet,
    Chunk.fromIterable,
    Chunk.map((code) => `${getWarehouseUuidByCode(code)}:${WAREHOUSE_STOCK_AVAILABILITY}`),
    Chunk.join(',')
  );

// v4: `Chunk.filterMap` now expects `(input, i) => Result<B, X>` (Result-shaped,
// not Option-shaped). Decoding here is purely synchronous string-parsing; walk
// the segments imperatively and collect the successes into a HashSet.
const apiStringToWarehouseFilterStateSet = (warehouseString: string): WarehouseFilterStateSet => {
  if (warehouseString === '') {
    return HashSet.empty();
  }

  const collected: Array<ReturnType<typeof getWarehouseCodeByUuid>> = [];
  for (const segment of Str.split(',')(warehouseString)) {
    if (segment.length === 0) continue;

    // Parse format: UUID:1
    const parts = segment.split(':');
    if (parts.length !== 2 || parts[1] !== WAREHOUSE_STOCK_AVAILABILITY) {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        Console.log(
          `[WarehouseFilter] Invalid segment format: "${segment}" (expected UUID:${WAREHOUSE_STOCK_AVAILABILITY})`
        ).pipe(() => undefined);
      }
      continue;
    }

    const uuid = parts[0];
    if (!uuid) {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        Console.log(`[WarehouseFilter] Missing UUID in segment: "${segment}"`).pipe(
          () => undefined
        );
      }
      continue;
    }

    const code = getWarehouseCodeByUuid(uuid);
    if (code) {
      collected.push(code);
    } else if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      Console.log(`[WarehouseFilter] Unknown warehouse UUID: "${uuid}"`).pipe(() => undefined);
    }
  }
  return HashSet.fromIterable(collected.filter((c): c is NonNullable<typeof c> => c !== undefined));
};

export const WarehouseApiQuery = Schema.Struct({
  warehouseStock: Schema.optional(Schema.String),
});

// v4: `Schema.decodeTo(target, transformation)` takes `{decode, encode}` where
// each is a `Getter`, not the v3 `SchemaTransformation.transform(...)` wrapper.
export const WarehouseToApi = Schema.toType(WarehouseFilterStateSetSchema).pipe(
  Schema.decodeTo(WarehouseApiQuery, {
    decode: SchemaGetter.transform((warehouseSet: WarehouseFilterStateSet) => {
      const warehouseString = warehouseFilterStateSetToApiString(warehouseSet);
      return warehouseString.length > 0 ? { warehouseStock: warehouseString } : {};
    }),
    encode: SchemaGetter.transform((apiData: { readonly warehouseStock?: string | undefined }) =>
      apiStringToWarehouseFilterStateSet(apiData.warehouseStock ?? '')
    ),
  })
);
